<script>
/**
 * 매수·매도(호가) 목록 페이지.
 *
 * 기능:
 * - 호가 목록 조회 (서버 페이지네이션)
 * - 검색 필터: 기간(기본 15일 전 ~ 1개월 후) / 구분(매수/매도) / 현물 구분(금/은) / 키워드(회원명)
 * - 행 클릭 → /trade/:p2pAskCode 상세 페이지 이동
 * - 공통 AppPagination 컴포넌트 재사용
 *
 * 권한:
 * - HQ 관리자: 전체 호가 목록 조회
 * - 지점 관리자: 자기 지점 관련 호가만 조회 (서버에서 자동 필터링)
 */
import { getTradeList } from '@/api/trade.js'
import AppPagination from '@/components/common/AppPagination.vue'
import AdminShell from '@/components/admin/layout/AdminShell.vue'
import AdminFilterBar from '@/components/admin/filter/AdminFilterBar.vue'
import AdminDateRange from '@/components/admin/form/AdminDateRange.vue'
import AdminDataTable from '@/components/admin/table/AdminDataTable.vue'
import AdminStatusBadge from '@/components/admin/table/AdminStatusBadge.vue'
import AdminLoadingState from '@/components/admin/feedback/AdminLoadingState.vue'
import AdminEmptyState from '@/components/admin/feedback/AdminEmptyState.vue'

export default {
  name: 'TradeListPage',

  components: {
    AppPagination,
    AdminShell,
    AdminFilterBar,
    AdminDateRange,
    AdminDataTable,
    AdminStatusBadge,
    AdminLoadingState,
    AdminEmptyState,
  },

  data() {
    /**
     * 기본 검색 기간 계산.
     * 디폴트: 현재날짜 15일 전 ~ 현재날짜 1달 후
     */
    const toDate = (d) => d.toISOString().substring(0, 10)
    const today = new Date()
    const defaultStart = new Date(today)
    defaultStart.setDate(defaultStart.getDate() - 15)
    const defaultEnd = new Date(today)
    defaultEnd.setMonth(defaultEnd.getMonth() + 1)

    return {
      /* ── 검색 조건 ── */
      search: {
        keyword: '',
        tradeSide: '',
        spotType: '',
        startDate: toDate(defaultStart),
        endDate: toDate(defaultEnd),
      },

      /** 구분 select 옵션 (BUY/SELL) */
      tradeSideOptions: [
        { title: '전체', value: '' },
        { title: '매수', value: 'BUY' },
        { title: '매도', value: 'SELL' },
      ],

      /** 현물 구분 select 옵션 (GOLD/SILVER) */
      spotTypeOptions: [
        { title: '전체', value: '' },
        { title: '금', value: 'GOLD' },
        { title: '은', value: 'SILVER' },
      ],

      /* ── 목록 데이터 ── */
      items: [],

      /* ── 페이지네이션 ── */
      page: 1,
      size: 10,
      total: 0,

      /* ── UI 상태 ── */
      loading: false,
    }
  },

  watch: {
    '$route.query': {
      handler() {
        this.applySearchFromRoute()
        this.fetchList()
      },
      deep: true,
    },
  },

  mounted() {
    this.applySearchFromRoute()
    this.fetchList()
  },

  computed: {
    filterFields() {
      return [
        { key: 'period', type: 'custom', wide: true },
        {
          key: 'tradeSide',
          type: 'select',
          label: '구분',
          options: this.tradeSideOptions,
          itemTitle: 'title',
          itemValue: 'value',
        },
        {
          key: 'spotType',
          type: 'select',
          label: '현물 구분',
          options: this.spotTypeOptions,
          itemTitle: 'title',
          itemValue: 'value',
        },
        {
          key: 'keyword',
          type: 'text',
          label: '회원명',
          placeholder: '회원명',
        },
      ]
    },
    dateRangeModel() {
      return {
        from: this.search.startDate,
        to: this.search.endDate,
      }
    },
    tableColumns() {
      return [
        { key: 'no', title: '번호', align: 'center', width: '90px' },
        { key: 'p2pAskCode', title: '호가코드', align: 'center' },
        { key: 'userName', title: '회원명', align: 'center' },
        { key: 'tradeSide', title: '구분', align: 'center' },
        { key: 'spotType', title: '현물 구분', align: 'center' },
        { key: 'weight', title: '중량(g)', align: 'center' },
        { key: 'tradeAmount', title: '거래금액', align: 'center' },
        { key: 'totalFeeAmount', title: '수수료', align: 'center' },
        { key: 'insertDt', title: '등록일', align: 'center' },
      ]
    },
  },

  methods: {
    /** 대시보드 등에서 ?startDate=&endDate=&tradeSide= 로 진입 시 검색조건 반영 */
    applySearchFromRoute() {
      const q = this.$route.query || {}
      if (q.startDate) this.search.startDate = String(q.startDate)
      if (q.endDate) this.search.endDate = String(q.endDate)
      if (q.tradeSide !== undefined && q.tradeSide !== null) this.search.tradeSide = String(q.tradeSide)
      if (q.spotType !== undefined && q.spotType !== null) this.search.spotType = String(q.spotType)
      if (q.keyword !== undefined && q.keyword !== null) this.search.keyword = String(q.keyword)
      const p = parseInt(String(q.page || ''), 10)
      if (!Number.isNaN(p) && p > 0) this.page = p
      const s = parseInt(String(q.size || ''), 10)
      if (!Number.isNaN(s) && s > 0) this.size = s
    },
    /**
     * 서버에서 매수·매도 목록을 조회한다.
     * 검색 조건 + 페이지네이션 파라미터를 전달하고 결과를 items / total에 세팅한다.
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await getTradeList({
          keyword: this.search.keyword || undefined,
          tradeSide: this.search.tradeSide || undefined,
          spotType: this.search.spotType || undefined,
          startDate: this.search.startDate || undefined,
          endDate: this.search.endDate || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          const data = res.data.data
          this.items = data?.items || []
          this.total = data?.total ?? 0
        }
      } catch (e) {
        console.error('[TradePage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },

    /** 검색 버튼 클릭: 페이지를 1로 리셋 후 조회 */
    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    /** 초기화 버튼 클릭: 검색 조건 + 페이지 초기화 후 재조회 */
    handleReset() {
      const toDate = (d) => d.toISOString().substring(0, 10)
      const today = new Date()
      const defaultStart = new Date(today)
      defaultStart.setDate(defaultStart.getDate() - 15)
      const defaultEnd = new Date(today)
      defaultEnd.setMonth(defaultEnd.getMonth() + 1)
      this.search = {
        keyword: '',
        tradeSide: '',
        spotType: '',
        startDate: toDate(defaultStart),
        endDate: toDate(defaultEnd),
      }
      this.page = 1
      this.fetchList()
    },

    /** 페이지 변경 (AppPagination emit) */
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    /** 페이지당 항목 수 변경 (AppPagination emit) */
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    /** 행 클릭 → 매수·매도 상세 페이지 이동 */
    goToDetail(item) {
      const row = item?.raw || item
      if (!row?.p2pAskCode) return
      this.$router.push(`/trade/${row.p2pAskCode}`)
    },

    /** 구분(BUY/SELL) chip 색상 반환 */
    getTradeSideColor(tradeSide) {
      return tradeSide === 'BUY' ? 'primary' : 'error'
    },

    /**
     * 날짜/시간 문자열을 'yyyy-MM-dd HH:mm' 형식으로 포맷한다.
     * null이면 '-' 반환.
     */
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },

    /**
     * 날짜 문자열을 'yyyy-MM-dd' 형식으로 포맷한다.
     * null이면 '-' 반환.
     */
    formatDate(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },

    /** 금액을 천단위 콤마 포맷으로 변환 */
    formatAmount(val) {
      if (val == null || val === '') return '-'
      return Number(val).toLocaleString()
    },
  },
}
</script>

<template>
  <AdminShell title="매수·매도 거래 내역">
    <p class="admin-page-subtitle">금·은 매수 및 매도 거래 이력 조회</p>

    <AdminFilterBar
      :model-value="search"
      :fields="filterFields"
      @update:model-value="search = { ...search, ...$event }"
      @search="handleSearch"
      @reset="handleReset"
    >
      <template #field-period>
        <AdminDateRange
          :model-value="dateRangeModel"
          from-label="시작일"
          to-label="종료일"
          @update:model-value="(v) => { search.startDate = v.from; search.endDate = v.to }"
        />
      </template>
    </AdminFilterBar>

    <div class="admin-page-card pa-4 mt-5">
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="text-subtitle-1 font-weight-bold">
          거래 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </div>
      </div>

      <AdminDataTable
        :columns="tableColumns"
        :items="items"
        row-key="p2pAskCode"
        :loading="loading"
        empty-text="검색 결과가 없습니다."
        @row-click="goToDetail"
      >
        <template #cell-no="{ index }">
          <span class="text-caption text-medium-emphasis">
            {{ total - (page - 1) * size - index }}
          </span>
        </template>

        <template #cell-p2pAskCode="{ item }">
          <span class="text-caption text-grey-darken-1 font-weight-medium">
            {{ (item?.raw || item)?.p2pAskCode || '-' }}
          </span>
        </template>

        <template #cell-userName="{ item }">
          <span class="font-weight-medium">{{ (item?.raw || item)?.userName || '-' }}</span>
        </template>

        <template #cell-tradeSide="{ item }">
          <AdminStatusBadge
            :status="(item?.raw || item)?.tradeSide || 'UNKNOWN'"
            :map="{
              BUY: { label: '매수', tone: 'info' },
              SELL: { label: '매도', tone: 'danger' },
              UNKNOWN: { label: '-', tone: 'default' },
            }"
            size="x-small"
          />
        </template>

        <template #cell-spotType="{ item }">
          <v-chip
            v-if="(item?.raw || item)?.spotType"
            size="x-small"
            :color="(item?.raw || item)?.spotType === 'GOLD' ? 'amber' : (item?.raw || item)?.spotType === 'SILVER' ? 'grey' : 'primary'"
            variant="tonal"
            label
          >
            {{ (item?.raw || item)?.spotType }}
          </v-chip>
          <span v-else class="text-caption text-medium-emphasis">-</span>
        </template>

        <template #cell-weight="{ item }">
          <span class="text-caption">{{ (item?.raw || item)?.weight != null ? (item?.raw || item)?.weight : '-' }}</span>
        </template>

        <template #cell-tradeAmount="{ item }">
          <span class="text-caption">{{ formatAmount((item?.raw || item)?.tradeAmount) }}</span>
        </template>

        <template #cell-totalFeeAmount="{ item }">
          <span class="text-caption">{{ formatAmount((item?.raw || item)?.totalFeeAmount) }}</span>
        </template>

        <template #cell-insertDt="{ item }">
          <span class="text-caption text-medium-emphasis">{{ formatDateTime((item?.raw || item)?.insertDt) }}</span>
        </template>

        <template #loading>
          <div class="pa-6">
            <AdminLoadingState text="거래 목록을 불러오는 중..." />
          </div>
        </template>

        <template #empty>
          <div class="pa-4">
            <AdminEmptyState title="검색 결과가 없습니다." />
          </div>
        </template>
      </AdminDataTable>

      <div class="mt-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </div>
  </AdminShell>
</template>
